<template>
  <div class="app-activation-tariff">
    <TariffDetails
      :show="modalShow"
      :tariff="selectedTariff"
      :extra-tariff-options-data="extraTariffOptionsData"
      @select-tariff="selectTariff"
    />

    <div class="button-container" v-show="modalShow">
      <button class="button button--secondary" @click="modalShow = false">
        Zurück
      </button>
      <button :disabled="loading" class="button button--primary" @click="selectTariff(selectedTariff)">
        {{ selectedTariff.name }} aktivieren
      </button>
    </div>

    <div class="app-tariff-carousel container-box" v-show="!modalShow">
      <div class="tariff-header-container">
        <h2 class="text-3xl">Unsere Tarife</h2>
        <div class="vodafone-logo">
          <img src="/files/lidl/images/new-images/vodafone.png" alt="Connected by Vodafone" />
        </div>
      </div>
      <div class="tabs">
        <div class="tab-buttons">
          <button
            class="button"
            :class="{
              'button--primary': activeTab === 'smart',
              'button--secondary': activeTab !== 'smart'
            }"
            @click="activeTab = 'smart'"
          >
            <div><span class="uppercase" lang="en">Smart</span>-Tarife ansehen</div>
          </button>
          <button
            class="button"
            :class="{
              'button--primary': activeTab === 'data',
              'button--secondary': activeTab !== 'data'
            }"
            @click="activeTab = 'data'"
          >
            <div><span class="uppercase" lang="en">Data</span>-Tarife ansehen</div>
          </button>
        </div>

        <div class="tab-content">
          <div v-show="activeTab === 'smart'">
            <VueSsrCarousel
              :slides-per-page="4"
              :gutter="8"
              show-arrows
              show-dots
              paginate-by-slide
              :responsive="[
                {
                  maxWidth: 1024,
                  slidesPerPage: 3
                },
                {
                  maxWidth: 767,
                  slidesPerPage: 2
                },
                {
                  maxWidth: 600,
                  slidesPerPage: 1
                }
              ]"
            >
              <div class="slide" v-for="(tariff, index) in smartTariffs" :key="tariff.tariffId" :index="index">
                <TariffSlide :tariff="tariff" @select-tariff="setSelected(tariff)"> </TariffSlide>
              </div>
            </VueSsrCarousel>
          </div>
          <div v-show="activeTab === 'data'">
            <VueSsrCarousel
              :slides-per-page="4"
              :gutter="8"
              show-arrows
              show-dots
              paginate-by-slide
              :responsive="[
                {
                  maxWidth: 1024,
                  slidesPerPage: 3
                },
                {
                  maxWidth: 767,
                  slidesPerPage: 2
                },
                {
                  maxWidth: 600,
                  slidesPerPage: 1
                }
              ]"
            >
              <div class="slide" v-for="(tariff, index) in dataTariffs" :key="tariff.tariffId" :index="index">
                <TariffSlide :tariff="tariff" @select-tariff="setSelected(tariff)"> </TariffSlide>
              </div>
            </VueSsrCarousel>
          </div>
        </div>
      </div>
    </div>
    <slot name="txtAfter"></slot>
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex'
import slotProxy from '@/mixins/slotProxy'
import processSteps from '@/mixins/processSteps'
import durationUnit from '@/mixins/durationUnit'
import price from '@/mixins/price'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import TariffDetails from './TariffDetails.vue'
import VueSsrCarousel from 'vue-ssr-carousel'
import TariffSlide from './TariffSlide.vue'

export const stepName = 'tariff'

export default {
  name: 'ActivationTariff',
  components: {
    TariffDetails,
    VueSsrCarousel,
    TariffSlide
  },
  mixins: [processSteps(stepName, stepName, stepName), slotProxy(), durationUnit, price],
  data: function() {
    return {
      show: false,
      loading: false,
      selectedTariff: {},
      modalShow: false,
      productInformations: {
        productInformationList: []
      },
      showAllOptions: false,
      activeTab: 'smart'
    }
  },
  props: {
    extra: {
      type: Array,
      default: () => []
    },
    extraTariffOptionsData: {
      type: [Object, Array],
      default: () => ({})
    }
  },
  computed: {
    ...mapGetters('activation/info', ['activatableTariffs']),
    ...mapGetters(['brand']),
    disabled: function() {
      return true
    },
    isInvisible: function() {
      let count = 0
      if (!(Object.keys(this.yearTariffs).length === 0)) count++
      if (!(Object.keys(this.dataTariffs).length === 0)) count++
      if (!(Object.keys(this.smartTariffs).length === 0)) count++
      if (!(Object.keys(this.comfortTariffs).length === 0)) count++
      if (!(Object.keys(this.youngTariffs).length === 0)) count++
      return count <= 1
    },
    hasDataTariffs: function() {
      return Object.keys(this.dataTariffs).length >= 1
    },
    hasSmartTariffs: function() {
      return Object.keys(this.smartTariffs).length >= 1
    },
    hasYearTariffs: function() {
      return Object.keys(this.yearTariffs).length >= 1
    },
    hasComfortTariffs: function() {
      return Object.keys(this.comfortTariffs).length >= 1
    },
    hasYoungTariffs: function() {
      return Object.keys(this.youngTariffs).length >= 1
    },
    fullTariffs: function() {
      return this.tariffs
    },
    tariffs: function() {
      return this.activatableTariffs.map(tariff => {
        const information = this.productInformations.productInformationList?.find(info => {
          return info.productIdentifier === tariff.tariffId
        })
        return {
          ...tariff,
          productInformation: information
        }
      })
    },
    classicTariffs: function() {
      return this.fullTariffs.filter(tariff => tariff.tariffType === 'CLASSIC')
    },
    dataTariffs: function() {
      return this.fullTariffs.filter(tariff => tariff.tariffType === 'DATA')
    },
    smartTariffs: function() {
      return this.fullTariffs.filter(tariff => tariff.tariffType === 'SMART')
    },
    comfortTariffs: function() {
      return this.fullTariffs.filter(tariff => tariff.tariffType === 'comfort')
    },
    youngTariffs: function() {
      return this.fullTariffs.filter(tariff => tariff.tariffType === 'young')
    },
    yearTariffs: function() {
      return this.fullTariffs.filter(tariff => tariff.tariffType === 'ANNUAL')
    },
    displayedTariffOptions() {
      if (!this.selectedTariff.allTariffOptions) return []
      return this.showAllOptions ? this.selectedTariff.allTariffOptions : this.selectedTariff.allTariffOptions.slice(0, 4)
    },
    hasMoreOptions() {
      return this.selectedTariff.allTariffOptions?.length > 4
    }
  },
  methods: {
    ...mapMutations('activation', ['goToStep']),
    ...mapActions('activation/tariff', {
      select: 'selectTariff'
    }),
    activateLoop() {
      return Object.keys(this.tariffs).length >= 3
    },
    selectTariff(tariff) {
      this.select({ tariff: tariff, nextStep: this.nextStep })
    },
    setSelected: function(selected) {
      this.modalShow = true
      this.selectedTariff = selected
    },
    closeModal() {
      this.show = false
    },
    showModal() {
      this.show = true
    },
    toggleShowMore() {
      this.showAllOptions = !this.showAllOptions
    }
  },
  apollo: {
    productInformations: function() {
      return wrapQuery(
        gql`
          query activation($brand: String!) {
            productInformations(brand: $brand) {
              productInformationList {
                active
                market
                pibName
                productIdentifier
                productName
                recentVersion {
                  documentReference
                  validFrom
                  validTo
                  version
                }
                historicVersions {
                  documentReference
                  validFrom
                  validTo
                  version
                }
                variant
              }
            }
          }
        `,
        this,
        'productInformations',
        { brand: this.brand }
      )
    }
  }
}
</script>
